<script setup lang="ts">
import { Fold, Search } from "@element-plus/icons-vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { ContactSearchItemDto, EntityDefinitionDto } from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { apiClient } from "~/utils/apiClient";
import CRMEntityDetail from "./CRMEntityDetail.vue";

const route = useRoute();
const myInfoStore = useMyInfoStore();
const orgInfoStore = useOrgInfoStore();

const contacts = ref<ContactSearchItemDto[]>([]);
const loading = ref(false);
const hasSearched = ref(false);
const searchValue = ref("");
const currentPage = ref(1);
const pageSize = ref(50);
const totalCount = ref(0);

// Drawer state
const selectedContact = ref<ContactSearchItemDto | null>(null);
const drawerVisible = ref(false);

function findEntityGuidByName(
  entities: EntityDefinitionDto[],
  name: string,
): string | null {
  for (const e of entities) {
    if (e.name === name) return e.guid;
    if (e.childEntityDefinitions?.length) {
      const found = findEntityGuidByName(e.childEntityDefinitions, name);
      if (found) return found;
    }
  }
  return null;
}

const entityDefinitionGuid = computed<string>(() => {
  if (route.meta.entityDefinitionGuid) {
    return route.meta.entityDefinitionGuid as string;
  }
  const name = (route.meta.entityName as string) ?? "Contact";
  const allEntities = orgInfoStore.MyOrgInfo?.entityDefinitionDtos ?? [];
  return findEntityGuidByName(allEntities, name) ?? "";
});

// ── Search field auto-detection (same pattern as OnHold.vue) ──

type SearchField = "name" | "email" | "phone";

const detectSearchField = (val: string): SearchField => {
  const v = val.trim();
  if (!v) return "name";
  if (v.includes("@")) return "email";
  const digits = v.replace(/\D/g, "");
  if (/[()+-]/.test(v) && digits.length >= 7) return "phone";
  if (/^\+?[\d\s\-().]{7,}$/.test(v) && digits.length >= 7) return "phone";
  return "name";
};

const detectedSearchField = computed<SearchField>(() =>
  detectSearchField(searchValue.value),
);

const searchFieldLabel = computed<string>(() => {
  const map: Record<SearchField, string> = {
    name: "Searching by name",
    email: "Searching by email",
    phone: "Searching by phone",
  };
  return map[detectedSearchField.value];
});

// ── Search ──

const canSearch = computed(() => searchValue.value.trim().length > 0);

const searchContacts = async (resetPage = true) => {
  if (!entityDefinitionGuid.value || !canSearch.value) return;

  if (resetPage) currentPage.value = 1;

  loading.value = true;
  hasSearched.value = true;
  try {
    const val = searchValue.value.trim() || undefined;
    const field = detectSearchField(searchValue.value);
    const skip = (currentPage.value - 1) * pageSize.value;

    const result = await apiClient.person.searchContacts(
      myInfoStore.currentSysOrgGuid,
      entityDefinitionGuid.value,
      field === "name" ? val : undefined,
      field === "email" ? val : undefined,
      field === "phone" ? val : undefined,
      skip,
      pageSize.value,
    );

    contacts.value = result.items ?? [];
    totalCount.value = result.totalCount ?? 0;
  } finally {
    loading.value = false;
  }
};

const handlePageChange = (page: number) => {
  currentPage.value = page;
  searchContacts(false);
};

const handleSizeChange = (size: number) => {
  pageSize.value = size;
  currentPage.value = 1;
  searchContacts(false);
};

const navigateToContact = (contact: ContactSearchItemDto) => {
  selectedContact.value = contact;
  drawerVisible.value = true;
};

watch(entityDefinitionGuid, async (newGuid) => {
  if (newGuid) {
    contacts.value = [];
    hasSearched.value = false;
    searchValue.value = "";
  }
});

onMounted(async () => {
  await Promise.all([orgInfoStore.load(), myInfoStore.load()]);
});
</script>

<template>
  <div class="ContactListingPage">
    <!-- ── Search bar ── -->
    <div class="search-bar">
      <div class="search-bar__main">
        <el-input
          v-model="searchValue"
          class="search-bar__input"
          placeholder="Search by name, email, or phone..."
          style="width: 380px"
          clearable
          :prefix-icon="Search"
          @keyup.enter="searchContacts"
        >
          <template #suffix>
            <span
              class="search-bar__detected-type"
              :data-field="detectedSearchField"
            >
              {{ searchValue ? searchFieldLabel : "" }}
            </span>
          </template>
        </el-input>

        <el-button
          type="primary"
          :disabled="!canSearch"
          :loading="loading"
          @click="() => searchContacts()"
        >
          Search
        </el-button>
      </div>
    </div>
    <!-- ── Pagination ── -->
    <el-pagination
      v-if="hasSearched && totalCount > 0"
      class="contact-pagination"
      :current-page="currentPage"
      :page-size="pageSize"
      :total="totalCount"
      :page-sizes="[25, 50, 100]"
      layout="total, sizes, prev, pager, next"
      @current-change="handlePageChange"
      @size-change="handleSizeChange"
    />
    <!-- ── Results table ── -->
    <el-table
      v-loading="loading"
      :data="contacts"
      class="contact-table"
      :default-sort="{ prop: 'name', order: 'ascending' }"
      @row-click="navigateToContact"
    >
      <template #empty>
        <div class="empty-state">
          <i class="mdi mdi-account-search-outline empty-icon" />
          <p>Use the search bar above to find contacts.</p>
        </div>
      </template>
      <el-table-column
        prop="name"
        label="Contact Name"
        sortable
        min-width="200"
      >
        <template #default="scope">
          <div class="contact-name-cell">
            <el-link type="primary" @click.stop="navigateToContact(scope.row)">
              <el-icon><Fold /></el-icon>
              {{ scope.row.name }}
            </el-link>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="status" label="Status" width="140" sortable>
        <template #default="scope">
          <el-tag v-if="scope.row.status" size="small" type="info">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        prop="primaryEmail"
        label="Email Address"
        min-width="220"
        sortable
      >
        <template #default="scope">
          <span v-if="scope.row.primaryEmail">
            {{ scope.row.primaryEmail }}
          </span>
          <span v-else class="text-muted">&mdash;</span>
        </template>
      </el-table-column>

      <el-table-column
        prop="primaryPhone"
        label="Phone Number"
        width="170"
        sortable
      >
        <template #default="scope">
          <span v-if="scope.row.primaryPhone">
            {{ scope.row.primaryPhone }}
          </span>
          <span v-else class="text-muted">&mdash;</span>
        </template>
      </el-table-column>
    </el-table>

    <!-- Contact Detail Drawer -->
    <el-drawer
      v-model="drawerVisible"
      :title="selectedContact?.name || 'Contact Details'"
      size="70%"
      @close="selectedContact = null"
    >
      <CRMEntityDetail
        v-if="selectedContact"
        :entity-definition-guid="entityDefinitionGuid"
        :entity-guid="selectedContact.entityGuid"
        :is-drawer="true"
      />
    </el-drawer>
  </div>
</template>

<style lang="less">
.ContactListingPage {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .list-header {
    .header-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.5rem;

      h2 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
      }
    }
  }

  .empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--el-text-color-secondary);
    margin: 2em 0;

    .empty-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      opacity: 0.5;
    }

    p {
      font-size: 1rem;
    }
  }

  .contact-pagination {
    justify-content: flex-end;
    padding: 12px 0 0;
    flex-shrink: 0;
  }

  .contact-table {
    flex: 1;
    // overflow: hidden;

    .contact-name-cell {
      display: flex;
      align-items: center;
      gap: 0.25rem;

      .el-icon {
        margin-right: 6px;
      }
    }
  }

  .text-muted {
    color: var(--el-text-color-placeholder);
  }

  :deep(.el-table__row) {
    cursor: pointer;

    &:hover {
      background-color: var(--el-fill-color-light);
    }
  }
  // ── Search bar (matches OnHold.vue) ──
  .search-bar {
    border-radius: 8px;
    background: var(--el-bg-color, #fff);
    overflow: hidden;

    &__main {
      display: flex;
      align-items: center;
      gap: 5px;
    }

    &__input {
      border: none;
      background: transparent;
      outline: none;
      color: var(--el-text-color-primary);
      width: 100%;

      &::placeholder {
        color: var(--el-text-color-placeholder);
      }

      .search-bar__detected-type {
        font-size: 11px;
        font-weight: 500;
        color: var(--el-text-color-placeholder);
        white-space: nowrap;
        transition: color 0.15s;

        &[data-field="name"],
        &[data-field="email"],
        &[data-field="phone"] {
          color: var(--el-color-primary);
        }
      }
    }

    &__divider {
      width: 1px;
      height: 22px;
      background: var(--el-border-color-lighter, #e4e7ed);
      flex-shrink: 0;
    }

    &__badge {
      min-width: 16px;
      height: 16px;
      border-radius: 8px;
      padding: 0 4px;
      background: var(--el-color-primary-light-8);
      color: var(--el-color-primary);
      font-size: 11px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__filters {
      border-top: 1px solid var(--el-border-color-lighter, #e4e7ed);
      padding: 10px 12px;
      display: flex;
      align-items: flex-end;
      gap: 12px;
      flex-wrap: wrap;
      background: var(--el-fill-color-lighter, #fafafa);
    }

    &__results {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-top: 1px solid var(--el-border-color-lighter, #e4e7ed);
      background: var(--el-fill-color-lighter, #fafafa);
      min-height: 36px;
    }
  }
}

.homeMain:has(.ContactListingPage) {
  overflow: hidden;
}

// Mobile
@media (max-width: 768px) {
  .ContactListingPage {
    padding: 0.75rem;
  }

  .search-bar {
    &__main {
      flex-wrap: wrap;
      height: auto;
      padding: 8px;
      gap: 6px;
    }

    &__filters {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>
